import { Box } from '@mui/material';

export type BenchmarksPerfBarProps = {
  /** 0–100 fill representing the brand's position in the cuisine peer group. */
  performance: number;
  /** 0–100 position of the cuisine-median tick. */
  baselineTick: number;
  /** Below baseline → red fill; otherwise green. */
  belowBaseline?: boolean;
  'data-testid'?: string;
};

const BAR_HEIGHT = 8;
/** Pixels the baseline tick extends past the bar, top and bottom. */
const TICK_OVERHANG = 4;

const clamp = (value: number) => Math.max(0, Math.min(100, value));

/**
 * Horizontal performance bar: a 0–100 fill colored by whether the brand is
 * above (green) or below (red) the cuisine baseline, with a tick marking the
 * cuisine median. The track clips the fill to its rounded ends, while the tick
 * lives in an outer (non-clipping) wrapper so it can overhang the bar.
 */
const BenchmarksPerfBar = ({
  performance,
  baselineTick,
  belowBaseline = false,
  'data-testid': dataTestId = 'Benchmarks.PerfBar',
}: BenchmarksPerfBarProps) => (
  <Box position="relative" data-testid={dataTestId}>
    <Box
      position="relative"
      height={BAR_HEIGHT}
      borderRadius={BAR_HEIGHT / 2}
      bgcolor="action.hover"
      overflow="hidden"
    >
      <Box
        position="absolute"
        left={0}
        top={0}
        bottom={0}
        width={`${clamp(performance)}%`}
        bgcolor={belowBaseline ? 'error.main' : 'success.main'}
        data-testid={`${dataTestId}.Fill`}
      />
    </Box>
    <Box
      position="absolute"
      top={`${-TICK_OVERHANG}px`}
      bottom={`${-TICK_OVERHANG}px`}
      left={`${clamp(baselineTick)}%`}
      width="2px"
      borderRadius="1px"
      bgcolor="text.primary"
      data-testid={`${dataTestId}.Tick`}
    />
  </Box>
);

export default BenchmarksPerfBar;
